import { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import { Card } from "./Ui/Card";
import dayjs from "dayjs";
import { Loader } from "lucide-react";
import TotalBalanceCard from "./TotalBalanceCard";
import { BalanceCard } from "./Wallet/BalanceCard";

const API_URL = `${process.env.REACT_APP_API_URL}/stats/get-stats`;

// Generate last 6 days + today
const generateLast7Days = () => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const date = dayjs().subtract(i, "day");
    days.push({
      rawDate: date.format("YYYY-MM-DD"),
      day: date.format("ddd DD MMM"),
      rc: 0,
      dl: 0,
      pan: 0,
      aadhaar: 0,
      voter: 0,
      aadhaarToPan: 0, // New
      rcToNumber: 0, // New
    });
  }
  return days;
};

// Custom Tooltip Component - Light Theme
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="text-gray-900 font-semibold mb-3 text-sm">{label}</p>
        {payload.map((entry, index) => (
          <div
            key={index}
            className="flex items-center gap-3 text-xs mb-2 last:mb-0"
          >
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-gray-600 font-medium min-w-fit">
              {entry.name}:
            </span>
            <span style={{ color: entry.color }} className="font-bold">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Legend Component - Light Theme
const CustomLegend = (props) => {
  const { payload } = props;
  return (
    <div className="flex flex-wrap gap-3 justify-center mt-6 pb-2">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          ></div>
          <span className="text-xs sm:text-sm text-gray-600 font-medium">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function DownloadStatsChart() {
  const [lineData, setLineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.role) {
      setUserRole(storedUser.role);
    }

    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");

        const { data } = await axios.get(API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const raw = data?.last7DaysStats || {};

        const merged = generateLast7Days().map((d) => {
          const rc = raw["rc"]?.find((i) => i.label === d.rawDate)?.count || 0;
          const dl =
            raw["driving-license"]?.find((i) => i.label === d.rawDate)?.count ||
            0;
          const pan =
            raw["pan"]?.find((i) => i.label === d.rawDate)?.count || 0;
          const aadhaar =
            raw["aadhaar"]?.find((i) => i.label === d.rawDate)?.count || 0;
          const voter =
            raw["voter-id"]?.find((i) => i.label === d.rawDate)?.count || 0;
          const aadhaarToPan =
            raw["aadhaar-to-pan"]?.find((i) => i.label === d.rawDate)?.count ||
            0;
          const rcToNumber =
            raw["rc-to-number"]?.find((i) => i.label === d.rawDate)?.count || 0;

          return {
            day: d.day,
            rc,
            dl,
            pan,
            aadhaar,
            voter,
            aadhaarToPan,
            rcToNumber,
          };
        });

        setLineData(merged);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch stats.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="space-y-6 w-full ">
      {/* Chart Card */}
      <Card className="w-full p-4 sm:p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        {/* Header Section */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
            Daily Downloads
          </h2>
          <p className="text-sm text-gray-500">
            Document downloads by day - Last 7 days
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64 sm:h-80 w-full">
            <div className="flex flex-col items-center gap-3">
              <Loader color="#3b82f6" size={32} className="animate-spin" />
              <p className="text-gray-500 text-sm">Loading chart data...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex justify-center items-center h-64 sm:h-80 w-full">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center max-w-sm">
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Chart */}
        {lineData.length > 0 && !loading && (
          <div className="w-full h-64 sm:h-80 -mx-4 sm:-mx-6 px-4 sm:px-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={lineData}
                margin={{ top: 10, right: 20, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  stroke="#9ca3af"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#6b7280" }}
                />
                <YAxis
                  stroke="#9ca3af"
                  style={{ fontSize: "12px" }}
                  tick={{ fill: "#6b7280" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />

                <Line
                  type="monotone"
                  dataKey="rc"
                  stroke="#9B8AFB"
                  strokeWidth={2.5}
                  dot={{ fill: "#9B8AFB", r: 4 }}
                  activeDot={{ r: 6, fill: "#9B8AFB" }}
                  name="RC"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="dl"
                  stroke="#FFD28F"
                  strokeWidth={2.5}
                  dot={{ fill: "#FFD28F", r: 4 }}
                  activeDot={{ r: 6, fill: "#FFD28F" }}
                  name="Driving License"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="pan"
                  stroke="#8EE0FF"
                  strokeWidth={2.5}
                  dot={{ fill: "#8EE0FF", r: 4 }}
                  activeDot={{ r: 6, fill: "#8EE0FF" }}
                  name="PAN"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="aadhaar"
                  stroke="#FF9E9E"
                  strokeWidth={2.5}
                  dot={{ fill: "#FF9E9E", r: 4 }}
                  activeDot={{ r: 6, fill: "#FF9E9E" }}
                  name="Aadhaar"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="voter"
                  stroke="#A0E7A0"
                  strokeWidth={2.5}
                  dot={{ fill: "#A0E7A0", r: 4 }}
                  activeDot={{ r: 6, fill: "#A0E7A0" }}
                  name="Voter ID"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="aadhaarToPan"
                  stroke="#FF6B9D"
                  strokeWidth={2.5}
                  dot={{ fill: "#FF6B9D", r: 4 }}
                  activeDot={{ r: 6, fill: "#FF6B9D" }}
                  name="Aadhaar to PAN"
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="rcToNumber"
                  stroke="#FFA500"
                  strokeWidth={2.5}
                  dot={{ fill: "#FFA500", r: 4 }}
                  activeDot={{ r: 6, fill: "#FFA500" }}
                  name="RC to Number"
                  isAnimationActive={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Empty State */}
        {lineData.length === 0 && !loading && !error && (
          <div className="flex justify-center items-center h-64 sm:h-80 w-full">
            <div className="text-center">
              <div className="text-4xl mb-3">📊</div>
              <p className="text-gray-700 font-medium">No data available</p>
              <p className="text-gray-500 text-sm mt-1">
                Download data will appear here
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Balance Cards Section */}
      {/* <div className="w-full">
        {userRole === "admin" ? (
          <TotalBalanceCard />
        ) : (
          <BalanceCard
            activeWallet="my"
            renderWalletTitle={() => "Your Wallet"}
          />
        )}
      </div> */}
    </div>
  );
}
